import { useContext } from 'react';
import classNames from 'classnames';
// import sendEvent from 'utils/experimentation';
import Link from 'next/link';
import ConditionalWrapper from 'utils/ConditionalWrapper';
import StyleContextProvider from 'utils/context/StyleContextProvider';
import { PromoContext } from 'utils/context/PromoContextProvider';
import { RowContext } from 'utils/context/RowContextProvider';
import { useViewport } from 'utils/context/ViewportContextProvider';
import { ENVIRONMENT, SHOPIFY } from 'utils/constants';
import useShopify from 'utils/hooks/useShopify';
import {
    base64toASCII,
    cBoolean,
    renderSection,
    formatPrice,
    checkSchedule,
    PointerArrowIcon as PointerArrow,
    PlusIcon as Plus,
    ShopPayIcon as ShopPay,
    parseURL,
} from '@viome/vnxt-nucleus';
import layoutClasses from 'components/Tailwind/Utils/LayoutClasses';
import PriceWrapper from 'components/Common/PriceCTA/PriceWrapper';
import Text from 'components/Tailwind/Module/Text';
import CartUpsellV2 from 'components/Tailwind/Components/CartUpsell/v2';
import * as Sentry from '@sentry/nextjs';
import { modalContext } from 'utils/context/DOMContextProvider';
import Popup from 'components/Tailwind/Components/PopupGeneric';
import styles from './CTA.module.scss';

const CTA = ({
    data: {
        mode,
        pricingMode = 'Light',
        backgroundColor,
        borderColor,
        renderAtMobile,
        renderAtTablet,
        renderAtDesktop,
        style,
        text,
        url,
        newTab,
        autoWidth = false,
        action,
        pricing,
        product,
        parameters,
        klaviyoFormId,
        discountAmount,
        discountCodeValue,
        salesMessage,
        layout,
        showPriceObservations = false,
        showMessageOnly = false,
        hideMessage = false,
        billingDetails,
        disableUpsell = false,
    },
    installments = false,
    position,
}) => {
    const { width } = useViewport(),
        renderConfig = { renderAtMobile, renderAtTablet, renderAtDesktop },
        showSection = renderSection(renderConfig, width),
        promoContext = useContext(PromoContext),
        rowContext = useContext(RowContext),
        layoutOptions = layoutClasses(
            layout,
            style === 'Custom' && borderColor,
            styles,
            style === 'Custom' && backgroundColor,
        ),
        isVariant = false,
        discountCode = promoContext?.discountCode,
        discountValue = promoContext?.discountValue,
        productFields = product?.fields || product,
        hasUpsell = productFields?.upsellSku,
        pricingOptions = {
            showPrice: cBoolean(pricing?.includes('Show Price')),
            showCurrency: cBoolean(pricing?.includes('Show Currency')),
            hideButton: cBoolean(pricing?.includes('Hide Button')),
            hideInstallments: cBoolean(pricing?.includes('Hide Installments')),
            right: cBoolean(pricing?.includes('To the right')),
            left: cBoolean(pricing?.includes('To the left')),
            larger: cBoolean(pricing?.includes('Larger Pricing')),
            hideSales:
                cBoolean(pricing?.includes('Hide Sales Message')) ||
                cBoolean(promoContext?.hidePromos),
            inside: cBoolean(pricing?.includes('Inside')),
            prefix: '$',
            discount:
                discountAmount || discountValue || productFields?.discount || 0,
            discountCode:
                discountCodeValue ||
                discountCode ||
                productFields?.discountCode ||
                null,
        },
        classes = classNames(
            styles.cta,
            'cta',
            [styles[mode], mode],
            style,
            styles[style],
            layoutOptions.classes,
            pricingOptions.showPrice && styles.showPrice,
            pricingOptions.inside && styles.inside,
            autoWidth && styles.autoWidth,
        ),
        { modal, setModal } = useContext(modalContext),
        handleKlaviyoCustomTrigger = (e) => {
            e.preventDefault();
            const _klOnsite = window._klOnsite || [];
            _klOnsite.push(['openForm', klaviyoFormId]);
        },
        ctaPosition = position || rowContext?.position || 'na';

    let ctaURL,
        isPlan = false,
        ctaOptions = {},
        price,
        showInstallment,
        productSKU,
        renderBody = text,
        salesMessageText,
        openNewTab = newTab;

    if (productFields) {
        productSKU = productFields?.sku;

        salesMessageText =
            salesMessage ||
            (pricingOptions.hideSales && promoContext?.salesMessage) ||
            (!pricingOptions.hideSales && productFields.salesMessage);

        const productShopify =
                (ENVIRONMENT === 'development' || ENVIRONMENT === 'preview'
                    ? productFields?.shopifyStaging
                    : productFields?.shopifyProduction) &&
                useShopify(productFields),
            ctaParameters =
                parameters ||
                (pricingOptions.discountCode &&
                    `discount=${pricingOptions.discountCode}`) ||
                '';

        let productId, variantId, planId, shopifyHost;

        if (ENVIRONMENT === 'production') {
            productId = base64toASCII(productFields.shopifyProduction);
            variantId = productFields?.shopifyId || productShopify?.variantID;
            shopifyHost = SHOPIFY.PRODUCTION_HOST;
            price = productShopify?.price || productFields?.price || 'N/A';
            planId = productFields.shopifySubscriptionPlanProd || '';
        } else {
            productId = base64toASCII(productFields.shopifyStaging);
            variantId =
                productFields?.shopifyIdStaging || productShopify?.variantID;
            shopifyHost = SHOPIFY.STAGING_HOST;
            price =
                productShopify?.price || productFields?.priceStaging || 'N/A';
            planId = productFields.shopifySubscriptionPlanStaging || '';
        }

        ctaOptions = {
            'data-name': productShopify?.name || productFields?.name,
            'data-id': productId,
            'data-sku': productSKU,
            'data-price': price !== 'N/A' ? parseFloat(price) : price,
            'data-quantity': 1,
        };

        if (productSKU === 'CD') {
            if (ENVIRONMENT === 'production') {
                ctaURL = `https://cancerdetect.viome.com`;
            } else {
                ctaURL = `https://cancerdetect-test.viome.com`;
            }
            openNewTab = true;
        } else {
            ctaURL = `https://${shopifyHost}.viome.com/pages/addtocart?id=${variantId}&plan=${planId}${
                ctaParameters ? `&${ctaParameters.replace('?', '')}` : ''
            }`;
        }

        if (planId) {
            isPlan = true;
        }

        showInstallment =
            (pricingOptions.showPrice &&
                !pricingOptions.hideInstallments &&
                ['HI', 'GI', 'FB', 'CD', 'OHI'].includes(productSKU)) ||
            installments;
    }
    const { salesMessageStartDate, salesMessageEndDate } = productFields || {},
        isSalesMessageActiveBySchedule = checkSchedule(
            salesMessageStartDate,
            salesMessageEndDate,
        );

    if (klaviyoFormId) {
        ctaOptions.onClick = handleKlaviyoCustomTrigger;
        ctaOptions.onKeyPress = handleKlaviyoCustomTrigger;
        ctaOptions.role = 'button';
        ctaOptions.tabIndex = 0;
    }

    ctaOptions['data-position'] = ctaPosition;

    if (style.includes('ForwardArrow')) {
        renderBody = (
            <>
                {text}
                <PointerArrow />
            </>
        );
    }
    if (style === 'Plus') {
        renderBody = (
            <>
                <Plus />
                {text}
            </>
        );
    }

    if (
        ENVIRONMENT === 'production' &&
        ((url && url.includes('staging-buy')) ||
            (ctaURL && ctaURL.includes('staging-buy')))
    ) {
        Sentry.captureMessage('Staging URL in Production Detected', {
            extra: {
                url,
                ctaURL,
                position: ctaPosition,
                ...(ctaOptions && { ctaOptions }),
            },
        });
        url = url ? parseURL(url) : '';
        ctaURL = ctaURL ? parseURL(ctaURL) : '';
    }

    const handleModal = () => {
            // https://viomeinc.atlassian.net/browse/JNY-2302 : Upsell A/B Test
            if (modal === null && hasUpsell) {
                setModal(
                    <Popup
                        closeState={() => {
                            setModal(null);
                        }}
                        redirectURL={ctaURL}
                        centerPopup
                        useCTA
                        mode="upsell"
                        sku={productSKU}
                    >
                        <CartUpsellV2
                            ctaUrl={url || ctaURL}
                            product={productFields}
                        />
                    </Popup>,
                );
            }
        },
        ctaClick = (
            e,
            // ctaOptions
        ) => {
            /* Launch Upsell/Popup if:
                 - Is Variant or Item has upsell enabled
                 - Upsell isn't disabled
                 - Is a product CTA
                */
            if (
                (isVariant || productFields?.upsell) &&
                !disableUpsell &&
                hasUpsell &&
                !url
            ) {
                e.preventDefault();
                handleModal();
            } else {
                // Only send add to cart events when necessary, otherwise
                // send cta events
                /*
                sendEvent(
                    productFields && !url ? 'add_to_cart' : 'cta_click',
                    productFields && !url ? ctaOptions['data-name'] : text,
                    ctaOptions,
                );
                */
            }
        },
        // https://viomeinc.atlassian.net/browse/JNY-2530: EXP008: Remove elements of Shop Page
        // https://docs.google.com/presentation/d/195WxojVB0ZKwT1m31vPYDk5n4bvdQoXj3W51nB_czxw/
        isProduct = url === '/products' || ctaURL === '/products',
        LinkElement = isProduct ? 'a' : Link;

    return (
        showSection && (
            <div
                className={classNames(
                    styles.wrapper,
                    'CTA__Wrapper',
                    pricingOptions.right && styles.right,
                    pricingOptions.left && styles.left,
                    pricingOptions.larger && styles.larger,
                    pricingOptions.showPrice && isPlan && styles.subscription,
                    pricingOptions.showPrice && styles.showPrice,
                    styles[mode],
                )}
                style={layoutOptions.rowStyles}
            >
                {!showMessageOnly && pricingOptions.showPrice && (
                    <StyleContextProvider styles={styles}>
                        <PriceWrapper
                            data={pricingOptions}
                            product={productFields}
                            isSubscription={isPlan}
                            pricingMode={pricingMode}
                            showPriceObservations={showPriceObservations}
                            billingDetails={billingDetails}
                        />
                    </StyleContextProvider>
                )}
                {salesMessageText &&
                    isSalesMessageActiveBySchedule &&
                    promoContext?.isPromoActiveBySchedule &&
                    !hideMessage && (
                        <div
                            className={classNames(
                                styles.salesMessage,
                                styles[pricingMode],
                                'salesMessage',
                                showMessageOnly && '!mb-0',
                            )}
                        >
                            {typeof salesMessageText === 'string' ? (
                                <p>
                                    <strong>{salesMessageText}</strong>
                                </p>
                            ) : (
                                <Text
                                    data={{
                                        body: salesMessageText,
                                        style: 'Left Aligned',
                                        bodyTextScaling: 'Smaller',
                                    }}
                                />
                            )}
                        </div>
                    )}
                {!showMessageOnly && !pricingOptions.hideButton && (
                    <>
                        {action ? (
                            <button
                                className={classes}
                                tabIndex="0"
                                type="submit"
                            >
                                {renderBody}
                            </button>
                        ) : (
                            <ConditionalWrapper
                                condition={url || ctaURL}
                                wrapper={(nodeChildData) => (
                                    <LinkElement
                                        href={url || ctaURL}
                                        className={classes}
                                        target={(openNewTab && '_blank') || ''}
                                        rel={
                                            (openNewTab &&
                                                'noopener noreferrer') ||
                                            ''
                                        }
                                        {...ctaOptions}
                                        style={layoutOptions.styles}
                                        onClick={(e) => ctaClick(e, ctaOptions)}
                                        onKeyDown={(e) =>
                                            ctaClick(e, ctaOptions)
                                        }
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {nodeChildData}
                                    </LinkElement>
                                )}
                                elseWrapper={(nodeChildData) => (
                                    <p
                                        className={classes}
                                        style={layoutOptions.styles}
                                    >
                                        {nodeChildData}
                                    </p>
                                )}
                            >
                                <span>
                                    {renderBody}{' '}
                                    {pricingOptions.inside && (
                                        <span>
                                            $
                                            {formatPrice(
                                                price,
                                                pricingOptions.discount || 0,
                                            )}
                                        </span>
                                    )}
                                </span>
                            </ConditionalWrapper>
                        )}
                        {showInstallment && (
                            <div
                                className={classNames(
                                    styles.showInstallment,
                                    'showInstallment',
                                    styles[pricingMode],
                                )}
                            >
                                <p>
                                    Pay in 4 interest-free payments with{' '}
                                    <span
                                        className={
                                            styles.showInstallment__image
                                        }
                                    >
                                        <ShopPay />
                                    </span>{' '}
                                    As low as{' '}
                                    <strong>
                                        $
                                        {formatPrice(
                                            price,
                                            pricingOptions.discount,
                                            4,
                                        )}
                                    </strong>
                                    <span
                                        title="The estimated payment amount excludes taxes and
                            shipping. Payment options are offered by Affirm and
                            are subject to eligibility check and might not be
                            available in all states. California residents:
                            Affirm Loan Services, LLC is licensed by the
                            Department of Business Oversight. Loans are made or
                            arranged pursuant to California Financing Law
                            license."
                                        aria-label="The estimated payment amount excludes taxes and
                            shipping. Payment options are offered by Affirm and
                            are subject to eligibility check and might not be
                            available in all states. California residents:
                            Affirm Loan Services, LLC is licensed by the
                            Department of Business Oversight. Loans are made or
                            arranged pursuant to California Financing Law
                            license."
                                    >
                                        *
                                    </span>
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        )
    );
};

export default CTA;
